import axios from 'axios';


// Use env variable, fallback to production backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://kingsolarman.pythonanywhere.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // allow sending cookies if you later add session auth
  withCredentials: false,
});

// Request interceptor for logging
api.interceptors.request.use((config) => {
  console.log(`🔄 API Call: ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Success: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const productService = {
  // Get all products with pagination + filters
  // params may include: page, page_size, ordering, search, brand, category, min_price, max_price
  getProducts: (params = {}) =>
    api.get('/products/', { params }).then(res => res.data),

  // Get single product
  getProduct: (id) =>
    api.get(`/products/${id}/`).then(res => res.data),

  // Search is implemented via the 'search' query param on the list endpoint
  searchProducts: (query, extraParams = {}) =>
    productService.getProducts({ ...extraParams, search: query }),

  // Get featured products (custom action)
  getFeatured: () =>
    api.get('/products/featured/').then(res => res.data),

  // Get on-sale products (custom action)
  getOnSale: () =>
    api.get('/products/on_sale/').then(res => res.data),

  // Get product categories (custom action)
  getCategories: () =>
    api.get('/products/categories/').then(res => res.data),

  // Get total product count (uses list endpoint metadata)
  getProductCount: async () => {
    const data = await api.get('/products/', { params: { page_size: 1 } }).then(r => r.data);
    return data.count ?? null;
  },
};

export const brandService = {
  // List of brands
  getAllBrands: () => api.get('/brands/').then(res => res.data),
  // Single brand
  getBrand: (id) => api.get(`/brands/${id}/`).then(res => res.data),
};

export const categoryService = {
  // categories are provided via product viewset action
  getAllCategories: () => api.get('/products/categories/').then(res => res.data),
};

export const healthService = {
  // If you have a health endpoint; otherwise you can call root or /api/
  checkHealth: () => api.get('/').then(res => res.status === 200),
};

// Frontend-only cart service stays as-is
export const cartService = {
  getCart: () => {
    const cart = localStorage.getItem('cart');
    return cart ? JSON.parse(cart) : [];
  },

  saveCart: (cartItems) => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  },

  addToCart: (product) => {
    const cart = cartService.getCart();
    const existingItem = cart.find(item => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    cartService.saveCart(cart);
    return Promise.resolve(cart);
  },

  removeFromCart: (productId) => {
    const cart = cartService.getCart();
    const updatedCart = cart.filter(item => item.id !== productId);
    cartService.saveCart(updatedCart);
    return Promise.resolve(updatedCart);
  },

  updateQuantity: (productId, quantity) => {
    const cart = cartService.getCart();
    const item = cart.find(item => item.id === productId);

    if (item) {
      item.quantity = quantity;
      if (item.quantity <= 0) {
        return cartService.removeFromCart(productId);
      }
      cartService.saveCart(cart);
    }

    return Promise.resolve(cart);
  },

  clearCart: () => {
    localStorage.removeItem('cart');
    return Promise.resolve([]);
  },
};



// Django API for order management
const DJANGO_API_BASE = 'https://kingsolarman.pythonanywhere.com/api';

const djangoApi = axios.create({
  baseURL: DJANGO_API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor for Django API
djangoApi.interceptors.request.use((config) => {
  console.log(`🔄 Django API Call: ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Response interceptor for Django API
djangoApi.interceptors.response.use(
  (response) => {
    console.log(`✅ Django API Success: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('❌ Django API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const orderService = {
  // Create a new order
  createOrder: (orderData) =>
    djangoApi.post('/orders/', orderData).then(res => res.data),

  // Get order by ID
  getOrder: (orderId) =>
    djangoApi.get(`/orders/${orderId}/`).then(res => res.data),

  // Track orders by customer email
  trackOrders: (email) =>
    djangoApi.post('/track-order/', { email }).then(res => res.data),

  // Update order status
  updateOrderStatus: (orderId, status, note) =>
    djangoApi.post('/orders/update-status/', { order_id: orderId, status, note }).then(res => res.data),

  // Get all orders (admin function)
  getAllOrders: () =>
    djangoApi.get('/orders/').then(res => res.data),
};

export default api;
